import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { ClassMember } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import ConfirmModal from './ConfirmModal';

interface SeatAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: ClassMember[];
  userId: string;
  classId: string;
  /**
   * 현재 자리배치 중인 멤버 그룹 키 (예: 'A', 'B')
   * - 명시하지 않으면 ClassMembersPartitionService가 localStorage 기반으로 추론
   * - 그룹별 자리배치를 정확히 분리하기 위해 가능한 한 전달하는 것이 안전함
   */
  memberGroup?: string;
  onSave: (updatedMembers: ClassMember[]) => void;
  onCopy?: (updatedMembers: ClassMember[]) => void;
}

interface DraggedStudent {
  member: ClassMember;
  offsetX: number;
  offsetY: number;
}

const GRID_ROWS = 16;
const GRID_COLS = 16;
const BASE_CELL_WIDTH = 50; // 기본 셀 가로 크기 (px)
const BASE_CELL_HEIGHT = 25; // 기본 셀 세로 크기 (px)
const STUDENT_BLOCK_SIZE = 2; // 멤버 블럭은 2x2 셀을 차지

export const SeatAssignmentModal: React.FC<SeatAssignmentModalProps> = ({
  isOpen,
  onClose,
  members,
  userId,
  classId,
  memberGroup,
  onSave,
  onCopy
}) => {
  console.log('🔍 SeatAssignmentModal 렌더링:', { isOpen, membersLength: members?.length, userId, classId, memberGroup });
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [seatArrangement, setSeatArrangement] = useState<Map<string, { row: number; col: number }>>(new Map());
  const [draggedStudent, setDraggedStudent] = useState<DraggedStudent | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{ row: number; col: number } | null>(null);
  const [isDragMode, setIsDragMode] = useState(false);
  const [dragStartTimer, setDragStartTimer] = useState<NodeJS.Timeout | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRandomized, setIsRandomized] = useState(false);
  const [beforeRandomArrangement, setBeforeRandomArrangement] = useState<Map<string, { row: number; col: number }>>(new Map());
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapTargetPosition, setSwapTargetPosition] = useState<{ row: number; col: number } | null>(null);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'center' | 'spread' | 'none'>('none');
  const [targetArrangement, setTargetArrangement] = useState<Map<string, { row: number; col: number }>>(new Map());
  const [selectedStudent, setSelectedStudent] = useState<ClassMember | null>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedCopyMonths, setSelectedCopyMonths] = useState<string[]>([]);
  const [initialSeatArrangement, setInitialSeatArrangement] = useState<Map<string, { row: number; col: number }>>(new Map());
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // 현재 월 초기화 및 사용 가능한 월 목록 계산
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setSelectedMonth(currentMonth);
      
      // 멤버들의 모든 저장된 월 목록 추출
      const months = new Set<string>();
      members.forEach(member => {
        if (member.seatPositions) {
          Object.keys(member.seatPositions).forEach(month => months.add(month));
        }
      });
      setAvailableMonths(Array.from(months).sort());
      
      // 모달이 열릴 때 뒷 페이지 스크롤 방지
      document.body.style.overflow = 'hidden';
    } else {
      // 모달이 닫힐 때 뒷 페이지 스크롤 복원
      document.body.style.overflow = 'unset';
    }
    
    // 컴포넌트 언마운트 시 스크롤 복원
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, members]);

  // 선택된 월이 변경되면 해당 월의 자리 배치 로드 (애니메이션 없이 즉시 변경)
  useEffect(() => {
    console.log('🔍 SeatAssignmentModal useEffect 실행 - selectedMonth:', selectedMonth, 'members.length:', members.length);
    if (selectedMonth && members.length > 0) {
      console.log('🔍 SeatAssignmentModal 자리 배치 로드 시작');
      // 애니메이션 상태 초기화
      setIsAnimating(false);
      setAnimationPhase('none');
      setTargetArrangement(new Map());

      const arrangement = new Map<string, { row: number; col: number }>();
      members.forEach(member => {
        if (member.seatPositions && member.seatPositions[selectedMonth]) {
          arrangement.set(member.id, member.seatPositions[selectedMonth]);
          console.log('🔍 SeatAssignmentModal 멤버 자리 배치 로드:', member.name, member.seatPositions[selectedMonth]);
        } else {
          console.log('🔍 SeatAssignmentModal 멤버 자리 배치 없음:', member.name);
        }
      });
      setSeatArrangement(arrangement);
      // 초기 상태 저장 (변경사항 감지용)
      setInitialSeatArrangement(new Map(arrangement));
      setShowCancelConfirm(false);
      console.log('🔍 SeatAssignmentModal 자리 배치 로드 완료 - 총 배치 수:', arrangement.size);
    }
  }, [selectedMonth, members]);

  // 변경사항 감지
  const hasChanges = useMemo(() => {
    if (initialSeatArrangement.size !== seatArrangement.size) return true;
    
    // 각 멤버의 위치 비교
    for (const [memberId, position] of Array.from(seatArrangement)) {
      const initialPosition = initialSeatArrangement.get(memberId);
      if (!initialPosition) return true; // 새로 배치됨
      if (initialPosition.row !== position.row || initialPosition.col !== position.col) {
        return true; // 위치 변경됨
      }
    }
    
    // 삭제된 배치 확인
    for (const [memberId] of Array.from(initialSeatArrangement)) {
      if (!seatArrangement.has(memberId)) return true; // 배치 제거됨
    }
    
    return false;
  }, [seatArrangement, initialSeatArrangement]);

  // 취소 핸들러
  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  // 특정 위치에 멤버가 있는지 확인 (2x2 블럭 고려)
  const isPositionOccupied = useMemo(() => {
    return (row: number, col: number, excludeStudentId?: string): boolean => {
      for (const [studentId, position] of Array.from(seatArrangement.entries())) {
        if (excludeStudentId && studentId === excludeStudentId) continue;
        
        // 2x2 블럭 영역 체크
        if (
          row >= position.row && row < position.row + STUDENT_BLOCK_SIZE &&
          col >= position.col && col < position.col + STUDENT_BLOCK_SIZE
        ) {
          return true;
        }
      }
      return false;
    };
  }, [seatArrangement]);

  // 멤버 블럭이 그리드 범위 내에 있는지 확인
  const isPositionValid = useMemo(() => {
    return (row: number, col: number): boolean => {
      return (
        row >= 0 && row <= GRID_ROWS - STUDENT_BLOCK_SIZE &&
        col >= 0 && col <= GRID_COLS - STUDENT_BLOCK_SIZE
      );
    };
  }, []);

  // 클래스 멤버에서 드래그 시작 (즉시)
  const handleClassMemberMouseDown = (member: ClassMember, e: React.MouseEvent) => {
    e.preventDefault();
    
    // currentTarget이 null일 수 있으므로 체크
    if (e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      setDraggedStudent({
        member,
        offsetX,
        offsetY
      });

      setDragPosition({
        x: e.clientX,
        y: e.clientY
      });

      setIsDragMode(true);
    }
  };

  // 그리드에서 드래그 시작 (1초 대기)
  const handleGridMouseDown = (member: ClassMember, e: React.MouseEvent) => {
    e.preventDefault();
    
    // 마우스 위치와 요소 정보를 미리 저장
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const clientX = e.clientX;
    const clientY = e.clientY;


    // 0.3초 후 드래그 모드 시작
    const timer = setTimeout(() => {
      setDraggedStudent({
        member,
        offsetX,
        offsetY
      });

      setDragPosition({
        x: clientX,
        y: clientY
      });

      setIsDragMode(true);
    }, 300);

    setDragStartTimer(timer);
  };

  // 마우스 업 (드래그 시작 취소)
  const handleMouseUp = () => {
    if (dragStartTimer) {
      clearTimeout(dragStartTimer);
      setDragStartTimer(null);
    }
    setIsDragMode(false);
    setSelectedStudent(null); // 드래그 모드 해제 시 선택된 멤버 정보도 초기화
  };

  // 마우스 리브 (드래그 시작 취소)
  const handleMouseLeave = () => {
    if (dragStartTimer) {
      clearTimeout(dragStartTimer);
      setDragStartTimer(null);
    }
    setIsDragMode(false);
    setSelectedStudent(null); // 드래그 모드 해제 시 선택된 멤버 정보도 초기화
  };

  // 마우스 이벤트 리스너 등록
  useEffect(() => {
    if (draggedStudent) {
      const handleDragMove = (e: MouseEvent) => {
        if (!gridRef.current) return;

        setDragPosition({
          x: e.clientX,
          y: e.clientY
        });

        const gridRect = gridRef.current.getBoundingClientRect();
        const x = e.clientX - gridRect.left;
        const y = e.clientY - gridRect.top;
        const cellWidth = gridRef.current.offsetWidth / GRID_COLS;
        const cellHeight = gridRef.current.offsetHeight / GRID_ROWS;

        if (x >= 0 && x < GRID_COLS * cellWidth && y >= 0 && y < GRID_ROWS * cellHeight) {
          const col = Math.floor(x / cellWidth);
          const row = Math.floor(y / cellHeight);
          const isValid = isPositionValid(row, col);
          const isOccupied = isPositionOccupied(row, col, draggedStudent.member.id);
          setPreviewPosition(isValid && !isOccupied ? { row, col } : null);
        } else {
          setPreviewPosition(null);
        }
      };

      const handleDragEnd = (e: MouseEvent) => {
        if (!gridRef.current) {
          setDraggedStudent(null);
          setDragPosition(null);
          setPreviewPosition(null);
          return;
        }

        const gridRect = gridRef.current.getBoundingClientRect();
        const x = e.clientX - gridRect.left;
        const y = e.clientY - gridRect.top;
        const cellWidth = gridRef.current.offsetWidth / GRID_COLS;
        const cellHeight = gridRef.current.offsetHeight / GRID_ROWS;

        const isInsideGrid =
          x >= 0 && x < GRID_COLS * cellWidth &&
          y >= 0 && y < GRID_ROWS * cellHeight;

        if (isInsideGrid) {
          const col = Math.floor(x / cellWidth);
          const row = Math.floor(y / cellHeight);
          if (isPositionValid(row, col) && !isPositionOccupied(row, col, draggedStudent.member.id)) {
            const newArrangement = new Map(seatArrangement);
            newArrangement.set(draggedStudent.member.id, { row, col });
            setSeatArrangement(newArrangement);
          }
        } else {
          // 그리드 밖으로 드롭하면 해당 학생의 자리 배치를 제거하여 상단 리스트로 되돌림
          const newArrangement = new Map(seatArrangement);
          if (newArrangement.has(draggedStudent.member.id)) {
            newArrangement.delete(draggedStudent.member.id);
            setSeatArrangement(newArrangement);
          }
        }

        setDraggedStudent(null);
        setDragPosition(null);
        setPreviewPosition(null);
        setIsDragMode(false);
        setSelectedStudent(null);
      };

      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [draggedStudent, isPositionOccupied, isPositionValid, seatArrangement]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (dragStartTimer) {
        clearTimeout(dragStartTimer);
      }
    };
  }, [dragStartTimer]);

  // 자리 배치 초기화
  const handleReset = () => {
    if (window.confirm('현재 월의 자리 배치를 초기화하시겠습니까?')) {
      setSeatArrangement(new Map());
      setIsRandomized(false);
      setBeforeRandomArrangement(new Map());
    }
  };

  // 애니메이션을 통한 랜덤 자리 배치
  const handleRandomArrangement = () => {
    try {
      // 현재 배치된 멤버들의 위치만 가져오기
      const placedPositions = Array.from(seatArrangement.values());
      
      if (placedPositions.length === 0) {
        alert('배치된 멤버가 없습니다.');
        return;
      }

      // 현재 배치 저장 (복구용)
      setBeforeRandomArrangement(new Map(seatArrangement));

      // 현재 배치된 멤버들의 ID 가져오기
      const placedStudentIds = Array.from(seatArrangement.keys());
      
      // Fisher-Yates shuffle 알고리즘으로 멤버 ID 섞기
      const shuffledIds = [...placedStudentIds];
      for (let i = shuffledIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledIds[i], shuffledIds[j]] = [shuffledIds[j], shuffledIds[i]];
      }

      // 섞인 멤버 ID를 원래 위치에 재배치
      const newArrangement = new Map<string, { row: number; col: number }>();
      shuffledIds.forEach((studentId, index) => {
        newArrangement.set(studentId, placedPositions[index]);
      });

      // 애니메이션 시작
      animateToArrangement(newArrangement);
      setIsRandomized(true);
    } catch (error) {
      console.error('랜덤 자리 배치 중 오류 발생:', error);
      alert('랜덤 자리 배치 중 오류가 발생했습니다.');
    }
  };

  // 애니메이션을 통한 원래 자리 복구
  const handleRestoreArrangement = () => {
    // 복구할 때는 중앙에 모이지 않고 바로 원래 위치로 이동
    animateToArrangementDirect(new Map(beforeRandomArrangement));
    setIsRandomized(false);
    setBeforeRandomArrangement(new Map());
  };

  // 그리드 중앙 좌표 계산
  const getGridCenter = (): { row: number; col: number } => {
    return {
      row: Math.floor(GRID_ROWS / 2) - 1, // 2x2 블럭을 고려하여 -1
      col: Math.floor(GRID_COLS / 2) - 1
    };
  };

  // 애니메이션 실행 함수 (중앙으로 모이기 + 퍼지기)
  const animateToArrangement = (newTargetArrangement: Map<string, { row: number; col: number }>) => {
    setIsAnimating(true);
    setTargetArrangement(newTargetArrangement);
    
    // 1단계: 중앙으로 모이기
    setAnimationPhase('center');
    
    setTimeout(() => {
      // 2단계: 최종 위치로 퍼지기
      setAnimationPhase('spread');
      
      // 애니메이션이 완전히 끝난 후에 실제 데이터를 업데이트
      setTimeout(() => {
        setSeatArrangement(newTargetArrangement);
        
        // 애니메이션 완료
        setAnimationPhase('none');
        setIsAnimating(false);
        setTargetArrangement(new Map());
        }, 600); // 퍼지는 애니메이션 시간
    }, 800); // 중앙으로 모이는 시간
  };

  // 직접 이동 애니메이션 함수 (중앙에 모이지 않고 바로 최종 위치로)
  const animateToArrangementDirect = (newTargetArrangement: Map<string, { row: number; col: number }>) => {
    setIsAnimating(true);
    setTargetArrangement(newTargetArrangement);
    
    // 바로 최종 위치로 이동 (중앙 단계 생략)
    setAnimationPhase('spread');
    
    // 애니메이션이 완전히 끝난 후에 실제 데이터를 업데이트
    setTimeout(() => {
      setSeatArrangement(newTargetArrangement);
      
      // 애니메이션 완료
      setAnimationPhase('none');
      setIsAnimating(false);
      setTargetArrangement(new Map());
    }, 600); // 퍼지는 애니메이션 시간
  };


  // 학기 범위: 현재가 1·2월이면 전년 3월~당해 2월, 그 외에는 당해 3월~다음해 2월
  const getSchoolYearRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1; // 1-12
    if (m <= 2) {
      return { startYear: y - 1, startMonth: 3, endYear: y, endMonth: 2 };
    }
    return { startYear: y, startMonth: 3, endYear: y + 1, endMonth: 2 };
  };

  // 월 네비게이션 (1년치 제한: 학기 기준 3월 ~ 다음해 2월)
  const navigateMonth = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedMonth + '-01');
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    
    let newYear = currentYear;
    let newMonth = currentMonth;
    
    if (direction === 'prev') {
      newMonth = currentMonth - 1;
      if (newMonth < 1) {
        newMonth = 12;
        newYear = currentYear - 1;
      }
    } else {
      newMonth = currentMonth + 1;
      if (newMonth > 12) {
        newMonth = 1;
        newYear = currentYear + 1;
      }
    }
    
    const range = getSchoolYearRange();
    const isInRange =
      newYear > range.startYear || (newYear === range.startYear && newMonth >= range.startMonth);
    const isInRangeEnd =
      newYear < range.endYear || (newYear === range.endYear && newMonth <= range.endMonth);
    
    if (isInRange && isInRangeEnd) {
      const newMonthStr = `${newYear}-${String(newMonth).padStart(2, '0')}`;
      setSelectedMonth(newMonthStr);
    }
  };

  // 월에 기록이 있는지 확인
  const hasRecordInMonth = (month: string): boolean => {
    return availableMonths.includes(month);
  };

  // 멤버 블럭 더블클릭 핸들러
  const handleStudentBlockDoubleClick = (position: { row: number; col: number }) => {
    setSwapTargetPosition(position);
    setShowSwapModal(true);
  };

  // 멤버 블럭 마우스 다운 핸들러
  const handleStudentBlockMouseDown = (member: ClassMember) => {
    setIsMouseDown(true);
    setSelectedStudent(member);
  };

  // 멤버 블럭 마우스 업 핸들러
  const handleStudentBlockMouseUp = () => {
    setIsMouseDown(false);
    // 약간의 지연 후 선택 해제 (드래그와 구분)
    setTimeout(() => {
      if (!isMouseDown) {
        setSelectedStudent(null);
      }
    }, 100);
  };

  // 자리배치 복제 관련 함수들
  const handleCopyClick = () => {
    setShowCopyModal(true);
    setSelectedCopyMonths([]);
  };

  const getAvailableMonths = (): string[] => {
    const range = getSchoolYearRange();
    const months: string[] = [];
    
    for (let year = range.startYear; year <= range.endYear; year++) {
      const startMonth = year === range.startYear ? range.startMonth : 1;
      const endMonth = year === range.endYear ? range.endMonth : 12;
      for (let month = startMonth; month <= endMonth; month++) {
        const monthStr = `${year}-${String(month).padStart(2, '0')}`;
        if (monthStr !== selectedMonth) {
          months.push(monthStr);
        }
      }
    }
    return months;
  };

  const toggleCopyMonth = (month: string) => {
    setSelectedCopyMonths(prev => 
      prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  };

  const handleCopyConfirm = async () => {
    if (selectedCopyMonths.length === 0) return;

    try {
      // 현재 자리배치를 선택된 월들에 복사
      const updatedMembers = members.map(member => {
        const currentPosition = seatArrangement.get(member.id);
        const baseSeatPositions = member.seatPositions || {};
        const seatPositions = { ...baseSeatPositions };
        
        // 선택된 월들에 현재 자리배치 복사
        selectedCopyMonths.forEach(month => {
          if (currentPosition) {
            seatPositions[month] = currentPosition;
          } else {
            // 현재 배치되지 않은 경우 해당 월에서 제거
            delete seatPositions[month];
          }
        });

        const hasSeatPositions = Object.keys(seatPositions).length > 0;
        return {
          ...member,
          ...(hasSeatPositions ? { seatPositions } : {})
        };
      });

      // Firebase에 저장 (현재 그룹 기준)
      await userDataService.saveClassMembersBatch(userId, classId, updatedMembers, memberGroup);
      
      console.log('✅ 자리배치 복사 완료');
      alert(`${selectedCopyMonths.length}개 월에 자리배치가 복사되었습니다.`);
      
      // 복사 후에는 모달을 닫지 않고 멤버 데이터만 업데이트
      if (onCopy) {
        onCopy(updatedMembers);
      } else {
        onSave(updatedMembers);
      }
      
      setShowCopyModal(false);
      setSelectedCopyMonths([]);
    } catch (error) {
      console.error('❌ 자리배치 복사 실패:', error);
      alert('자리배치 복사에 실패했습니다.');
    }
  };

  // 멤버 교환
  const handleSwapStudents = (swapStudentId: string) => {
    if (!swapTargetPosition) return;

    // 선택한 위치의 멤버 찾기
    let targetStudentId: string | null = null;
    for (const [studentId, position] of Array.from(seatArrangement.entries())) {
      if (position.row === swapTargetPosition.row && position.col === swapTargetPosition.col) {
        targetStudentId = studentId;
        break;
      }
    }

    if (!targetStudentId) return;

    // 교환할 멤버의 현재 위치 찾기
    const swapStudentPosition = seatArrangement.get(swapStudentId);

    const newArrangement = new Map(seatArrangement);

    if (swapStudentPosition) {
      // 둘 다 배치되어 있는 경우 위치 교환
      newArrangement.set(targetStudentId, swapStudentPosition);
      newArrangement.set(swapStudentId, swapTargetPosition);
    } else {
      // 교환할 멤버가 배치되어 있지 않은 경우
      newArrangement.delete(targetStudentId);
      newArrangement.set(swapStudentId, swapTargetPosition);
    }

    setSeatArrangement(newArrangement);
    setShowSwapModal(false);
    setSwapTargetPosition(null);
  };

  // 저장
  const handleSave = async () => {
    if (!selectedMonth) return;

    setIsSaving(true);
    try {
      // 멤버 데이터 업데이트
      const updatedMembers = members.map(member => {
        const position = seatArrangement.get(member.id);
        const baseSeatPositions = member.seatPositions || {};
        const seatPositions = { ...baseSeatPositions };
        
        if (position) {
          seatPositions[selectedMonth] = position;
        } else {
          // 배치되지 않은 경우 해당 월 정보 삭제
          delete seatPositions[selectedMonth];
        }

        const hasSeatPositions = Object.keys(seatPositions).length > 0;
        return {
          ...member,
          ...(hasSeatPositions ? { seatPositions } : {})
        };
      });

      // Firebase에 저장 (현재 그룹 기준)
      await userDataService.saveClassMembersBatch(userId, classId, updatedMembers, memberGroup);
      
      console.log('✅ 자리 배치 저장 완료');
      alert('자리 배치가 저장되었습니다.');
      onSave(updatedMembers);
      onClose();
    } catch (error) {
      console.error('❌ 자리 배치 저장 실패:', error);
      alert('자리 배치 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 배치되지 않은 멤버 목록
  const unassignedStudents = members.filter(member => !seatArrangement.has(member.id));

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitleCentered>클래스 자리 배정</ModalTitleCentered>
          <CloseButton onClick={handleCancel}>✕</CloseButton>
        </ModalHeader>

        <ModalContent>
          {/* 월 네비게이션 */}
          <MonthNavigation>
            <MonthNavButton onClick={() => navigateMonth('prev')}>
              ‹
            </MonthNavButton>
            <MonthDisplay $hasRecord={hasRecordInMonth(selectedMonth)}>
              <YearText>{new Date(selectedMonth + '-01').getFullYear()}</YearText>
              {new Date(selectedMonth + '-01').getMonth() + 1}월
            </MonthDisplay>
            <MonthNavButton onClick={() => navigateMonth('next')}>
              ›
            </MonthNavButton>
          </MonthNavigation>

          {/* 자리 배치 그리드 */}
          <Section>
            <SectionHeader>
              {/* 선택된 멤버 정보 표시 - 드래그 모드일 때만 표시 */}
              {selectedStudent && isDragMode ? (
                <SelectedStudentInfo>
                  {selectedStudent.number}번 {selectedStudent.name}
                </SelectedStudentInfo>
              ) : (
                <SectionTitle>클래스 자리 배치</SectionTitle>
              )}
              <ButtonGroup>
                <ResetButton onClick={handleReset} disabled={isAnimating}>
                  초기화
                </ResetButton>
                {isRandomized ? (
                  <RandomButton onClick={handleRestoreArrangement} disabled={isAnimating}>
                    원래 자리로 복구
                  </RandomButton>
                ) : (
                  <RandomButton onClick={handleRandomArrangement} disabled={isAnimating}>
                    랜덤 자리 배치
                  </RandomButton>
                )}
              </ButtonGroup>
            </SectionHeader>

            {/* 클래스 멤버 목록 - 배치되지 않은 멤버가 있을 때만 표시 */}
            {unassignedStudents.length > 0 && (
              <StudentListContainer>
                <StudentList>
                  {unassignedStudents.map(member => (
                    <StudentBlock
                      key={member.id}
                      onMouseDown={(e) => handleClassMemberMouseDown(member, e)}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseLeave}
                      $isDragging={draggedStudent?.member.id === member.id}
                      $isDragMode={isDragMode}
                    >
                      {String(member.number).padStart(2, '0')}번 {member.name}
                    </StudentBlock>
                  ))}
                </StudentList>
              </StudentListContainer>
            )}

            <GridContainer ref={gridRef}>
              {/* 그리드 배경 */}
              <GridBackground>
                {Array.from({ length: GRID_ROWS }).map((_, row) =>
                  Array.from({ length: GRID_COLS }).map((_, col) => (
                    <GridCell key={`${row}-${col}`} />
                  ))
                )}
              </GridBackground>

              {/* 드래그 프리뷰 (그리드 위에서) */}
              {previewPosition && draggedStudent && (
                <PreviewBlock
                  style={{
                    top: `calc(${previewPosition.row} * var(--cell-height))`,
                    left: `calc(${previewPosition.col} * var(--cell-width))`,
                  }}
                >
                  <div className="student-number">{draggedStudent.member.number}번</div>
                  <div className="student-name">{draggedStudent.member.name}</div>
                </PreviewBlock>
              )}

              {/* 배치된 멤버들 */}
              {Array.from(seatArrangement.entries()).map(([studentId, position]) => {
                const member = members.find(m => m.id === studentId);
                if (!member) return null;

                // 현재 드래그 중인 멤버은 표시하지 않음
                if (draggedStudent?.member.id === studentId) return null;

                // 애니메이션 위치 계산
                let displayPosition = position;
                if (isAnimating) {
                  if (animationPhase === 'center') {
                    // 중앙으로 모이기 - 모든 블럭이 중앙으로
                    displayPosition = getGridCenter();
                  } else if (animationPhase === 'spread') {
                    // 퍼지는 단계에서는 targetArrangement에서 최종 위치를 가져옴
                    const targetPosition = targetArrangement.get(studentId);
                    displayPosition = targetPosition || position;
                  }
                }

                return (
                  <PlacedStudentBlock
                    key={studentId}
                    style={{
                      top: `calc(${displayPosition.row} * var(--cell-height))`,
                      left: `calc(${displayPosition.col} * var(--cell-width))`,
                    }}
                    onMouseDown={(e) => {
                      // 그리드에서는 0.3초 대기 후 드래그 시작
                      handleGridMouseDown(member, e);
                      handleStudentBlockMouseDown(member);
                    }}
                    onMouseUp={(e) => {
                      handleMouseUp();
                      handleStudentBlockMouseUp();
                    }}
                    onMouseLeave={(e) => {
                      handleMouseLeave();
                      handleStudentBlockMouseUp();
                    }}
                    onDoubleClick={() => handleStudentBlockDoubleClick(position)}
                    $isDragMode={isDragMode}
                    $isAnimating={isAnimating}
                    $animationPhase={animationPhase}
                  >
                    <div className="student-number">{String(member.number).padStart(2, '0')}번</div>
                    <div className="student-name">{member.name}</div>
                  </PlacedStudentBlock>
                );
              })}
            </GridContainer>

            {/* 자리배치 액션 버튼들 */}
            <SeatActionButtonRow>
              <SeatDeleteButton onClick={handleReset}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SeatDeleteButton>
              <SeatCopyButton onClick={handleCopyClick}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SeatCopyButton>
            </SeatActionButtonRow>
          </Section>
        </ModalContent>

        <ModalFooter>
          <FooterButton data-variant="modal-secondary" onClick={handleCancel} $variant="secondary">
            닫기
          </FooterButton>
          <FooterButton onClick={handleSave} $variant="primary" disabled={isSaving}>
            {isSaving ? '저장 중...' : '저장'}
          </FooterButton>
        </ModalFooter>

        {/* 드래그 중인 멤버 블럭 */}
        {draggedStudent && dragPosition && (
          <DraggingBlock
            style={{
              left: dragPosition.x - draggedStudent.offsetX,
              top: dragPosition.y - draggedStudent.offsetY,
            }}
          >
            <div className="student-number">{draggedStudent.member.number}번</div>
            <div className="student-name">{draggedStudent.member.name}</div>
          </DraggingBlock>
        )}
      </ModalContainer>


      {/* 자리배치 복제 모달 */}
      {showCopyModal && (
        <>
          <SeatCopyModalOverlay onClick={() => setShowCopyModal(false)} />
          <SeatCopyModalContainer onClick={(e) => e.stopPropagation()}>
            <SeatCopyModalTitle>복사할 월을 선택하세요</SeatCopyModalTitle>
            <MonthSelector>
              {getAvailableMonths().map(month => (
                <MonthButton
                  key={month}
                  isSelected={selectedCopyMonths.includes(month)}
                  onClick={() => toggleCopyMonth(month)}
                >
                  {new Date(month + '-01').getMonth() + 1}월
                </MonthButton>
              ))}
            </MonthSelector>
            <SeatCopyModalFooter>
              <SeatCopyModalCloseButton 
                data-variant="modal-secondary"
                onClick={() => setShowCopyModal(false)}
              >
                닫기
              </SeatCopyModalCloseButton>
              <SeatCopyModalSaveButton 
                onClick={handleCopyConfirm}
                disabled={selectedCopyMonths.length === 0}
              >
                복사
              </SeatCopyModalSaveButton>
            </SeatCopyModalFooter>
          </SeatCopyModalContainer>
        </>
      )}

      {/* 멤버 교환 모달 */}
      {showSwapModal && swapTargetPosition && (
        <>
          <ConfirmModalOverlay onClick={() => setShowSwapModal(false)} />
          <ConfirmModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmModalTitle>자리배정 멤버 선택</ConfirmModalTitle>
            <SwapStudentList>
              {members.map(member => (
                <SwapStudentItem
                  key={member.id}
                  onClick={() => handleSwapStudents(member.id)}
                >
                  {member.number}번 {member.name}
                </SwapStudentItem>
              ))}
            </SwapStudentList>
            <ConfirmModalFooter>
              <ConfirmModalButton 
                $variant="secondary" 
                onClick={() => {
                  setShowSwapModal(false);
                  setSwapTargetPosition(null);
                }}
              >
                취소
              </ConfirmModalButton>
            </ConfirmModalFooter>
          </ConfirmModalContainer>
        </>
      )}

      {/* 취소 확인 모달 */}
      {showCancelConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              onClose();
            }}
            onCancel={() => {
              setShowCancelConfirm(false);
              handleSave();
            }}
            title={[
              "변경사항이 있습니다.",
              "저장하지 않고 나가시겠습니까?",
              "변경사항이 모두 사라집니다."
            ]}
            confirmText="나가기"
            cancelText="저장"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  padding: 120px 0; /* 상하 여백을 더 넉넉하게 */
  overflow-y: auto; /* 전체 페이지 스크롤 활성화 */
`;

const ModalContainer = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90vw; /* 화면 너비의 90%로 고정 */
  max-width: ${GRID_COLS * BASE_CELL_WIDTH + 100}px; /* 최대 크기 제한 */
  margin: 0 auto; /* 중앙 정렬 */
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  min-height: fit-content; /* 내용에 맞게 높이 조정 */
  
  /* CSS 변수로 셀 크기 동적 조정 - 버튼 경계에 맞춤 */
  --cell-width: calc((90vw - 48px) / ${GRID_COLS}); /* ModalContent 좌우 패딩 24px씩 제외 후 16등분 */
  --cell-height: calc(var(--cell-width) / 2); /* 2:1 비율 유지 */
  
  /* 최대 크기 제한 */
  @media (min-width: ${(GRID_COLS * BASE_CELL_WIDTH + 100) / 0.9}px) {
    --cell-width: ${BASE_CELL_WIDTH}px;
    --cell-height: ${BASE_CELL_HEIGHT}px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const ModalTitleCentered = styled(ModalTitle)`
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const ModalContent = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const StudentListContainer = styled.div`
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  min-height: 120px;
  width: calc(${GRID_COLS} * var(--cell-width)); /* 그리드와 동일한 너비 */
  align-self: center;
`;

const StudentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const StudentBlock = styled.div<{ $isDragging?: boolean; $isDragMode?: boolean }>`
  background: var(--color-surface);
  border: 2px solid var(--seat-block-border-color);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-primary);
  cursor: ${props => props.$isDragMode ? 'grabbing' : 'grab'};
  user-select: none;
  transition: all 0.2s;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  min-width: 50px;
  text-align: center;
  
  &:hover {
    background: var(--color-accent-soft);
    transform: ${props => props.$isDragMode ? 'scale(0.95)' : 'translateY(-2px)'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    cursor: grabbing;
  }
`;



const GridContainer = styled.div`
  position: relative;
  width: calc(${GRID_COLS} * var(--cell-width));
  height: calc(${GRID_ROWS} * var(--cell-height));
  border: 2px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-accent-soft); /* 연한 테마 초록 배경 */
  align-self: center;
`;

const GridBackground = styled.div`
  display: grid;
  grid-template-columns: repeat(${GRID_COLS}, var(--cell-width));
  grid-template-rows: repeat(${GRID_ROWS}, var(--cell-height));
  width: 100%;
  height: 100%;
`;

const GridCell = styled.div`
  border: 1px solid var(--seat-grid-line-color);
`;

const PlacedStudentBlock = styled.div<{ $isDragMode?: boolean; $isAnimating?: boolean; $animationPhase?: string }>`
  position: absolute;
  width: calc(${STUDENT_BLOCK_SIZE} * var(--cell-width));
  height: calc(${STUDENT_BLOCK_SIZE} * var(--cell-height));
  background: var(--color-surface); /* 흰색 배경 */
  color: var(--color-text-primary);
  border: 2px solid var(--seat-block-border-color);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  cursor: ${props => props.$isDragMode ? 'grabbing' : 'grab'};
  user-select: none;
  text-align: center;
  line-height: 1.2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .student-number {
    font-size: calc(var(--cell-width) * 0.18); /* 숫자는 작게 */
    margin-bottom: 2px;
  }
  
  .student-name {
    font-size: calc(var(--cell-width) * 0.32); /* 이름은 더 크게 */
    font-weight: 700;
  }
  
  /* 화면 가로 600px 이하에서 번호 숨기고 이름만 표시 */
  @media (max-width: 600px) {
    padding: 2px; /* 내부 패딩 최소화 */
    
    .student-number {
      display: none;
    }
    
    .student-name {
      font-size: calc(var(--cell-width) * 0.5); /* 이름을 더 크게 */
      margin: 0;
      line-height: 1;
    }
  }
  transition: ${props => {
    if (props.$isAnimating) {
      if (props.$animationPhase === 'center') {
        return 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      } else if (props.$animationPhase === 'spread') {
        return 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
    }
    return 'none'; /* 애니메이션이 아닐 때는 transition 없음 */
  }};
  
  /* 애니메이션 중에는 transition이 항상 적용되도록 */
  ${props => props.$isAnimating && `
    will-change: transform, left, top;
  `}
  
  &:hover {
    transform: ${props => {
      if (props.$isAnimating) return 'none';
      return props.$isDragMode ? 'scale(0.97)' : 'scale(1.03)';
    }};
    box-shadow: ${props => {
      if (props.$isAnimating) return '0 1px 3px rgba(0, 0, 0, 0.1)';
      return '0 2px 8px rgba(0, 0, 0, 0.15)';
    }};
    z-index: ${props => props.$isAnimating ? '1' : '10'};
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const PreviewBlock = styled.div`
  position: absolute;
  width: calc(${STUDENT_BLOCK_SIZE} * var(--cell-width));
  height: calc(${STUDENT_BLOCK_SIZE} * var(--cell-height));
  background: var(--seat-preview-bg-color);
  color: var(--seat-preview-text-color);
  border: 2px dashed var(--seat-preview-border-color);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  pointer-events: none;
  z-index: 5;
  animation: pulse 1s ease-in-out infinite;
  
  .student-number {
    font-size: calc(var(--cell-width) * 0.18);
    margin-bottom: 2px;
  }
  
  .student-name {
    font-size: calc(var(--cell-width) * 0.32);
    font-weight: 700;
  }
  
  @media (max-width: 600px) {
    padding: 2px; /* 내부 패딩 최소화 */
    
    .student-number {
      display: none;
    }
    
    .student-name {
      font-size: calc(var(--cell-width) * 0.5);
      margin: 0;
      line-height: 1;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
`;

const DraggingBlock = styled.div`
  position: fixed;
  width: calc(${STUDENT_BLOCK_SIZE} * var(--cell-width) * 0.85); /* 85% 크기 */
  height: calc(${STUDENT_BLOCK_SIZE} * var(--cell-height) * 0.85); /* 85% 크기 */
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 2px solid var(--color-accent-strong);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  pointer-events: none;
  z-index: 10001;
  opacity: 0.9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  .student-number {
    font-size: calc(var(--cell-width) * 0.15);
    margin-bottom: 1px;
  }
  
  .student-name {
    font-size: calc(var(--cell-width) * 0.25);
    font-weight: 700;
  }
  
  @media (max-width: 600px) {
    padding: 2px; /* 내부 패딩 최소화 */
    
    .student-number {
      display: none;
    }
    
    .student-name {
      font-size: calc(var(--cell-width) * 0.4);
      margin: 0;
      line-height: 1;
    }
  }
`;

const ResetButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 16px;
  height: 26px;
  min-height: 26px;
  background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-reset-button-text)'};
  border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-reset-button-text)'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-reset-button-bg)'};
  }

  &:active {
    transform: ${props => props.disabled ? 'none' : 'scale(0.98)'};
  }
`;

const RandomButton = styled(ResetButton)`
  /* 랜덤 버튼도 동일한 스타일 사용 */
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
`;

const FooterButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent-strong);
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    &:disabled {
      background: var(--color-accent);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: transparent;
    color: var(--color-accent);
    border: 1px solid var(--color-accent);

    &:hover {
      background: rgba(var(--color-accent-rgb), 0.1);
      border-color: var(--color-accent);
    }

    &:active {
      transform: translateY(0);
    }
  `}
  
  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

// 월 네비게이션 스타일
const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const MonthNavButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: 2px solid var(--color-accent-strong);
  background: var(--color-surface);
  color: var(--color-accent-text);
  border-radius: 50%;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0; /* 버튼이 줄어들지 않도록 */

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MonthDisplay = styled.div<{ $hasRecord: boolean }>`
  min-width: 80px;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  
  ${props => props.$hasRecord ? `
    background: var(--color-accent-strong);
    color: white;
  ` : `
    background: transparent;
    color: var(--color-accent-text);
  `}
`;

const YearText = styled.span`
  font-size: 12px;
  font-weight: 400;
  opacity: 0.6;
  line-height: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const SelectedStudentInfo = styled.div`
  background: #000;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin: 0 auto;
  min-width: 80px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

// 자리배치 액션 버튼들
const SeatActionButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

const SeatDeleteButton = styled.button`
  width: 120px;
  height: 40px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-panel);
  color: var(--color-text-gray);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 100px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 360px) {
    width: 80px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  &:hover {
    background: var(--color-border);
  }
  
  &:focus {
    outline: none;
  }
`;

const SeatCopyButton = styled.button`
  width: 120px;
  height: 40px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-panel);
  color: var(--color-text-gray);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 100px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 360px) {
    width: 80px;
    height: 32px;
    font-size: 0.75rem;
  }
  
  &:hover {
    background: var(--color-border);
  }
  
  &:focus {
    outline: none;
  }
`;

// 자리배치 복제 모달 스타일
const SeatCopyModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
`;

const SeatCopyModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 10002;
  width: 90vw;
  max-width: 500px;
  padding: 24px;
`;

const SeatCopyModalTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
`;

const MonthSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  justify-content: center;
`;

const MonthButton = styled.button<{ isSelected: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  background: ${props => props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.isSelected ? 'white' : 'var(--color-text-gray)'};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 60px;

  &:hover {
    border-color: var(--color-accent-text);
    background: ${props => props.isSelected ? 'var(--color-accent-strong)' : 'rgba(var(--color-accent-rgb), 0.1)'};
    color: ${props => props.isSelected ? 'white' : 'var(--color-accent-strong)'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SeatCopyModalFooter = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SeatCopyModalCloseButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const SeatCopyModalSaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

// 확인 모달 스타일
const ConfirmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
`;

const ConfirmModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 10002;
  width: 90vw;
  max-width: 400px;
  padding: 24px;
`;

const ConfirmModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const ConfirmModalFooter = styled.div`
  display: flex;
  width: 100%;
`;

const ConfirmModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 10px 20px;
  background: ${props => props.$variant === 'primary' ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$variant === 'primary' ? 'white' : '#6c757d'};
  border: ${props => props.$variant === 'primary' ? 'none' : '1px solid var(--color-border)'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$variant === 'primary' ? 'rgba(var(--color-accent-rgb), 0.9)' : 'var(--color-panel)'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

// 멤버 교환 모달 스타일
const SwapStudentList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 24px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
`;

const SwapStudentItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: var(--color-text-primary);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    color: var(--color-accent-text);
  }

  &:active {
    background: rgba(var(--color-accent-rgb), 0.2);
  }
`;
